import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import useUserContext from '../../context/user';
import useCartContext from '../../context/cart';
import { Alert, Button, Form, Typography } from 'components/base';
import useModalContext from 'components/context/modal';
import usersApi from '../../api/users';

const DeleteAcount = (props) => {
  const modalContext = useModalContext();
  const userContext = useUserContext();
  const cartContext = useCartContext();
  const router = useRouter();

  const handleSignOut = () => {
    modalContext.confirm(<>Your account was permanently deleted.</>, {
      title: 'Delete Account',
      closeable: false,
      confirmLabel: 'OK',
    });

    router.push('/');

    userContext.setAccessToken(null);
    userContext.clear();
    cartContext.clear();
  };

  const showCodeModal = (code) => {
    // Body owns the form state. The provider footer reads "submit" via a
    // closure-captured ref so Cancel/Confirm sit on the proper chrome footer
    // surface (gray edge-to-edge) instead of a nested `<Modal.Footer>` inside
    // the body, which produces an inset gray pill under the chrome skin.
    const submitRef = { current: null };

    modalContext.show(
      <CodeForm
        accessToken={props.accessToken}
        code={code}
        submitRef={submitRef}
        onSuccess={handleSignOut}
      />,
      {
        title: 'Delete Account',
        footer: ({ close }) => (
          <>
            <Button type="outline" onClick={close}>
              Cancel
            </Button>
            <Button type={props.buttonType} onClick={() => submitRef.current?.(close)}>
              Confirm
            </Button>
          </>
        ),
      },
    );
  };

  const handleRequestCode = async () => {
    const { data, error } = await usersApi(props.accessToken).requestDeleteAccount();

    if (error) {
      modalContext.toast(error, { type: 'warning' });
      return;
    }

    if (data) {
      showCodeModal(data.code);
    }
  };

  const handleDeleteAccountClick = () => {
    modalContext.confirm('Do you really want to delete your account?', {
      title: 'Delete Account',
      confirmLabel: 'Continue',
      type: props.buttonType,
      onConfirm: handleRequestCode,
    });
  };

  return (
    <div className={props.className}>
      <Typography.H3 className="mt-5 mb-2">Danger Zone</Typography.H3>
      <Typography.P>Permanently delete this account and all associated data.</Typography.P>
      <Button type={props.buttonType} onClick={handleDeleteAccountClick}>
        Delete Account
      </Button>
    </div>
  );
};

const CodeForm = ({ accessToken, code, submitRef, onSuccess }) => {
  const [input, setInput] = useState('');
  const [err, setErr] = useState(null);
  const [isInvalid, setIsInvalid] = useState(false);

  // Latest-handler ref: re-published on every render so the footer's Confirm
  // button always invokes the freshest closure (current `input`, `code`, …).
  const handlerRef = useRef();
  handlerRef.current = async (close) => {
    if (code !== input) {
      setErr('The code you entered is incorrect');
      setIsInvalid(true);
      return;
    }

    setErr(null);

    const { data, error } = await usersApi(accessToken).deleteAccount(input);

    if (error) {
      setErr(error);
    } else if (data) {
      onSuccess();
      close();
    }
  };

  useEffect(() => {
    submitRef.current = (...args) => handlerRef.current(...args);
    return () => {
      submitRef.current = null;
    };
  }, [submitRef]);

  return (
    <>
      <Form.Group>
        <p>
          To confirm, please enter the code <strong>{code}</strong> below:
        </p>
        <Form.Input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setIsInvalid(false);
          }}
          isInvalid={isInvalid}
        />
      </Form.Group>
      {err && (
        <Form.Group>
          <Alert type="danger">{err}</Alert>
        </Form.Group>
      )}
    </>
  );
};

CodeForm.propTypes = {
  accessToken: PropTypes.string,
  code: PropTypes.string,
  submitRef: PropTypes.shape({ current: PropTypes.func }),
  onSuccess: PropTypes.func,
};

DeleteAcount.propTypes = {
  accessToken: PropTypes.string,
  className: PropTypes.string,
  buttonType: PropTypes.string,
  children: PropTypes.node,
};

export default DeleteAcount;
